import { toFullDate } from "@@/shared/date-time/full-date";
import { type Timezone, tz } from "@@/shared/date-time/timezone";
import type { AccountingData } from "@@/shared/financials/accounting/accounting-data";
import type { Provider } from "@@/shared/providers/provider";
import {
    type Transaction,
    isOrderChangeRefund,
} from "@@/shared/settlements/transactions";
import { svTranslator } from "@@/shared/translations/translation";
import { groupBy } from "@@/utilities/group-by";
import { sum } from "@@/utilities/sum";
import { generateTransactionLines_SALES } from "./sie-generator-transaction-lines-sales";
import { generateTransactionLines_VAT } from "./sie-generator-transaction-lines-vat";

const creationDate = new Date(2021, 4, 27);

//TODO byt ut provider mot provider-data
const generateSie = (params: {
    provider: Provider;
    transactions: Transaction[];
    providerAccountingData: AccountingData;
    /**
     * defaults to `Europe/Stockholm`
     * @type {string}
     */
    timeZone?: Timezone;
}) => {
    const { transactions, provider, providerAccountingData } = params;
    const timeZone = params.timeZone ?? tz.EuropeStockholm;
    const grouped = groupBy(transactions, transaction =>
        toFullDate(transaction._createdAt, timeZone).toString(),
    );

    const vouchers = [...grouped.keys()].flatMap(fullDate => {
        // #VER serie vernr verdatum vertext regdatum sign
        const versLine = `#VER "" "" ${fullDate} ""`;
        const lines = grouped.get(fullDate) ?? [];
        const transactionLines =
            lines.flatMap(transaction => {
                //If order change, the value of the order items are already corrected
                const orderChangedTransaction =
                    isOrderChangeRefund(transaction);

                const refund =
                    transaction.transactionType === "REFUND" &&
                    !orderChangedTransaction
                        ? -1
                        : 1;

                const items = generateTransactionLines_SALES(
                    transaction,
                    refund,
                    providerAccountingData,
                );
                const vat = generateTransactionLines_VAT(
                    transaction,
                    providerAccountingData,
                    refund,
                );
                return [...items, ...vat];
            }) ?? [];
        if (!transactionLines.length) return [];

        // Räkna ut "fordringskonto" hur mycket pengar är Towni skyldiga
        const accountReceivableSum = sum(
            lines.map(line => {
                // Reversed if refund the account should increase as Towni owe them less money
                return line.transactionAmount;
            }),
        );
        const line = `#TRANS ${providerAccountingData.accountReceivable} { } ${
            accountReceivableSum / 100
        }`;

        return [versLine, `{`, line, ...transactionLines, `}`];
    });

    const sieData = [
        `#FLAGGA 0`,
        `#PROGRAM "Alfred Technologies" 1`,
        `#FORMAT PC8`,
        `#GEN ${toFullDate(creationDate, timeZone)} SIGNATUR`,
        `#SIETYP 4`,
        `#FNAMN "${svTranslator(provider.meta.title)}"`,
        ...vouchers,
    ].join("\n");
    return sieData;
};

export { generateSie };
